import { useState } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Textarea } from "@/components/ui/textarea";
import { Loader2, Building2, Shield, ArrowRight, CheckCircle2 } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";

interface BankDetailsDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  organizerEmail: string;
  onSuccess: () => void;
}

export function BankDetailsDialog({
  open,
  onOpenChange,
  organizerEmail,
  onSuccess,
}: BankDetailsDialogProps) {
  const [saving, setSaving] = useState(false);
  
  // Bank account fields
  const [accountHolderName, setAccountHolderName] = useState("");
  const [accountNumber, setAccountNumber] = useState("");
  const [confirmAccountNumber, setConfirmAccountNumber] = useState("");
  const [ifscCode, setIfscCode] = useState("");
  const [bankName, setBankName] = useState("");
  
  // Contact and address details for payout verification
  const [phone, setPhone] = useState("");
  const [address, setAddress] = useState("");
  const [city, setCity] = useState("");
  const [state, setState] = useState("");
  const [pincode, setPincode] = useState("");

  // Validation
  const isPhoneValid = /^[6-9]\d{9}$/.test(phone);
  const isIfscValid = /^[A-Z]{4}0[A-Z0-9]{6}$/.test(ifscCode);
  const isPincodeValid = /^\d{6}$/.test(pincode);

  const isBankValid = 
    accountHolderName.length >= 3 &&
    accountNumber.length >= 9 &&
    accountNumber === confirmAccountNumber &&
    isIfscValid;

  const isKycValid = 
    isPhoneValid &&
    address.length >= 10 &&
    city.length >= 2 &&
    state.length >= 2 &&
    isPincodeValid;

  const isFormValid = isBankValid && isKycValid;

  const handleSubmit = async () => {
    if (!isFormValid) return;
    
    setSaving(true);
    try {
      // Create or update razorpay_linked_accounts record with full KYC
      const accountData = {
        organizer_email: organizerEmail.toLowerCase(),
        recipient_name: accountHolderName,
        recipient_type: "organizer",
        account_holder_name: accountHolderName,
        bank_account_number: accountNumber,
        ifsc_code: ifscCode.toUpperCase(),
        bank_name: bankName || null,
        phone: phone,
        address_line1: address,
        city: city,
        state: state,
        pincode: pincode,
        account_status: "pending_verification",
      };

      // Check if account already exists
      const { data: existing } = await supabase
        .from("razorpay_linked_accounts")
        .select("id")
        .eq("organizer_email", organizerEmail.toLowerCase())
        .maybeSingle();

      if (existing) {
        // Update existing
        const { error } = await supabase
          .from("razorpay_linked_accounts")
          .update(accountData)
          .eq("id", existing.id);
        
        if (error) throw error;
      } else {
        // Insert new
        const { error } = await supabase
          .from("razorpay_linked_accounts")
          .insert(accountData);
        
        if (error) throw error;
      }

      toast.success("Payment details saved! Verification in progress.");
      onSuccess();
      onOpenChange(false);
    } catch (error) {
      console.error("Error saving payment details:", error);
      toast.error("Failed to save payment details");
    } finally {
      setSaving(false);
    }
  };

  const handleSkip = () => {
    toast.info("You can add payment details later from Settings");
    onSuccess();
    onOpenChange(false);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-lg max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <div className="mx-auto w-12 h-12 rounded-full bg-emerald-500/10 flex items-center justify-center mb-2">
            <Building2 className="h-6 w-6 text-emerald-600" />
          </div>
          <DialogTitle className="text-center">Set Up Payouts</DialogTitle>
          <DialogDescription className="text-center">
            Add your bank account details to receive collected funds via Razorpay Route
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-5 py-4">
          {/* Bank Account Section */}
          <div className="space-y-4">
            <div className="flex items-center gap-2 text-sm font-medium text-foreground">
              <div className="w-6 h-6 rounded-full bg-emerald-500 text-white flex items-center justify-center text-xs">1</div>
              Bank Account Details
            </div>
            
            <div className="space-y-3 pl-8">
              <div className="space-y-2">
                <Label htmlFor="accountHolder">Account Holder Name *</Label>
                <Input
                  id="accountHolder"
                  placeholder="As per bank records"
                  value={accountHolderName}
                  onChange={(e) => setAccountHolderName(e.target.value)}
                />
              </div>
              
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                <div className="space-y-2">
                  <Label htmlFor="accountNumber">Account Number *</Label>
                  <Input
                    id="accountNumber"
                    placeholder="Enter account number"
                    value={accountNumber}
                    onChange={(e) => setAccountNumber(e.target.value.replace(/\D/g, ""))}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="confirmAccount">Confirm Account *</Label>
                  <Input
                    id="confirmAccount"
                    placeholder="Re-enter number"
                    value={confirmAccountNumber}
                    onChange={(e) => setConfirmAccountNumber(e.target.value.replace(/\D/g, ""))}
                  />
                </div>
              </div>
              {confirmAccountNumber && accountNumber !== confirmAccountNumber && (
                <p className="text-xs text-destructive">Account numbers don't match</p>
              )}
              
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-2">
                  <Label htmlFor="ifsc">IFSC Code *</Label>
                  <Input
                    id="ifsc"
                    placeholder="SBIN0001234"
                    value={ifscCode}
                    onChange={(e) => setIfscCode(e.target.value.toUpperCase())}
                    maxLength={11}
                  />
                  {ifscCode && !isIfscValid && (
                    <p className="text-xs text-destructive">Invalid IFSC format</p>
                  )}
                </div>
                <div className="space-y-2">
                  <Label htmlFor="bankName">Bank Name</Label>
                  <Input
                    id="bankName"
                    placeholder="SBI, HDFC..."
                    value={bankName}
                    onChange={(e) => setBankName(e.target.value)}
                  />
                </div>
              </div>
            </div>
          </div>

          {/* KYC Section */}
          <div className="space-y-4">
            <div className="flex items-center gap-2 text-sm font-medium text-foreground">
              <div className="w-6 h-6 rounded-full bg-emerald-500 text-white flex items-center justify-center text-xs">2</div>
              KYC Verification
            </div>
            
            <div className="space-y-3 pl-8">
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-2">
                  <Label htmlFor="phone">Phone Number *</Label>
                  <Input
                    id="phone"
                    placeholder="9876543210"
                    value={phone}
                    onChange={(e) => setPhone(e.target.value.replace(/\D/g, ""))}
                    maxLength={10}
                  />
                  {phone && !isPhoneValid && (
                    <p className="text-xs text-destructive">Invalid phone number</p>
                  )}
                </div>
              </div>
              
              <div className="space-y-2">
                <Label htmlFor="address">Address *</Label>
                <Textarea
                  id="address"
                  placeholder="Street address, building, landmark"
                  value={address}
                  onChange={(e) => setAddress(e.target.value)}
                  rows={2}
                />
              </div>
              
              <div className="grid grid-cols-3 gap-3">
                <div className="space-y-2">
                  <Label htmlFor="city">City *</Label>
                  <Input
                    id="city"
                    placeholder="Mumbai"
                    value={city}
                    onChange={(e) => setCity(e.target.value)}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="state">State *</Label>
                  <Input
                    id="state"
                    placeholder="Maharashtra"
                    value={state}
                    onChange={(e) => setState(e.target.value)}
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="pincode">Zip Code / Postal Code *</Label>
                  <Input
                    id="pincode"
                    placeholder="400001"
                    value={pincode}
                    onChange={(e) => setPincode(e.target.value.replace(/\D/g, ""))}
                    maxLength={6}
                  />
                  {pincode && !isPincodeValid && (
                    <p className="text-xs text-destructive">Invalid postal code</p>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* Validation Summary */}
          <div className="bg-muted/50 rounded-lg p-3 space-y-2">
            <div className="text-xs font-medium text-muted-foreground mb-2">Verification checklist</div>
            <div className="grid grid-cols-2 gap-2 text-xs">
              <div className={`flex items-center gap-1.5 ${isBankValid ? "text-emerald-600" : "text-muted-foreground"}`}>
                <CheckCircle2 className={`h-3.5 w-3.5 ${isBankValid ? "opacity-100" : "opacity-40"}`} />
                Bank details complete
              </div>
              <div className={`flex items-center gap-1.5 ${isPhoneValid ? "text-emerald-600" : "text-muted-foreground"}`}>
                <CheckCircle2 className={`h-3.5 w-3.5 ${isPhoneValid ? "opacity-100" : "opacity-40"}`} />
                Phone verified
              </div>
              <div className={`flex items-center gap-1.5 ${address.length >= 10 && isPincodeValid ? "text-emerald-600" : "text-muted-foreground"}`}>
                <CheckCircle2 className={`h-3.5 w-3.5 ${address.length >= 10 && isPincodeValid ? "opacity-100" : "opacity-40"}`} />
                Address complete
              </div>
            </div>
          </div>

          <Alert className="border-emerald-500/20 bg-emerald-500/5">
            <Shield className="h-4 w-4 text-emerald-600" />
            <AlertDescription className="text-xs">
              Your details are encrypted and used only for Razorpay Route verification. 
              Funds are transferred directly to your bank account after payment collection.
            </AlertDescription>
          </Alert>
        </div>

        <div className="flex gap-3">
          <Button variant="ghost" onClick={handleSkip} className="flex-1">
            Skip for now
          </Button>
          <Button
            onClick={handleSubmit}
            disabled={!isFormValid || saving}
            className="flex-1 bg-emerald-600 hover:bg-emerald-700"
          >
            {saving ? (
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            ) : null}
            Save & Verify
            <ArrowRight className="ml-2 h-4 w-4" />
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

// Helper function to check if user has bank details configured
export async function checkBankDetailsConfigured(email: string): Promise<boolean> {
  try {
    const { data, error } = await supabase
      .from("razorpay_linked_accounts")
      .select("id")
      .eq("organizer_email", email.toLowerCase())
      .maybeSingle();
    
    if (error) throw error;
    return !!data;
  } catch (error) {
    console.error("Error checking bank details:", error);
    return false;
  }
}
